import { camelCase } from 'lodash';
import naturalCompare from 'natural-compare-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
// import { useSelector } from 'react-redux';
import { FieldSectionType } from 'api/field-sections/types';
// import { CompanyId, ProjectId, UserStatus, UserType } from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import {
  Field,
  // FieldOption
} from 'api/tasks';
import {
  // FieldDataType,
  KeyEventsAndIntervalFieldTypes,
} from 'components/organisms/module-settings/fields/types';
import {
  FieldType,
  filterableFieldType,
  SourceType,
} from 'components/organisms/tasks/fields';
// import { RootState } from 'data';
import {
  useDoesPartnerMatchPermissionName,
  useIsCurrentUserPartner,
} from 'hooks/use-partner';
// import { useUser } from 'hooks/use-user';
import {
  useIntervalsFieldsQueryV2,
  useKeyEventsFieldsQueryV2,
  useProjectFieldsQuery,
  useProjectFieldsQueryV2,
} from 'queries';
import { useProjectsFromInitData } from 'queries/account';
// import { useUsersQuery } from 'queries/members';
// import { constructUserMeta } from 'stores/fields/util';
// import { UsersStore } from 'stores/users';
// import { userOption } from './construct-user-option';

export const default_project_fields = [
  'projectName',
  'startDate',
  'dueDate',
  'customer',
  'projectOwner',
  'projectId',
  'projectFee',
  'inferredProgress',
  'currency',
  'customerChampion',
  'phaseId',
  'annualizedRecurringRevenue',
  'budgetedMinutes',
];
const feature_specific_project_fields = ['budgetedHours'];
const getSourceType = (field: Field): SourceType => {
  if (field.objectType === FieldSectionType.Project) {
    return SourceType.Project;
  }
  if (field.objectType === FieldSectionType.Users) {
    return SourceType.User;
  }

  return SourceType.Task;
};

export const nonDefaultField = (f: Field): boolean =>
  !default_project_fields.includes(camelCase(f.fieldName));

const statusField = (f: Field): boolean =>
  f.customField && f.fieldName.toLowerCase() === 'status';

const nonFeatureField = (f: Field): boolean =>
  !feature_specific_project_fields.includes(camelCase(f.fieldName));

export const isSelectTypeField = (field: Field): boolean => {
  const type = filterableFieldType(field);

  return [
    FieldType.SingleSelect,
    FieldType.MultiSelect,
    FieldType.SingleUser,
    FieldType.MultiUser,
  ].includes(type);
};

interface FieldWithSource extends Field {
  sourceType: SourceType;
}

export const useProjectFieldByName = (fieldName: string) => {
  const { data } = useProjectFieldsQuery();

  return useMemo(
    () =>
      data?.find(
        (field) =>
          field?.fieldName?.toLowerCase() === fieldName?.toLowerCase() &&
          field?.objectType === FieldSectionType.Project,
      ),
    [data, fieldName],
  );
};

export const useProjectFields = () => {
  const { data, isLoading: isProjectFieldsLoading } = useProjectFieldsQueryV2();
  const { data: projectData } = useProjectsFromInitData();

  // const { data: allUsers } = useUsersQuery();
  // const { user: currentUser } = useUser();
  const projectIdsFromQuery = projectData?.projects?.map((x) => x.projectId);

  const [projectIds, setProjectIds] = useState(projectIdsFromQuery);

  useEffect(() => {
    // tried using useSelector with memozing it didn't help since it recomputed the projectid.
    flushSync(() => {
      setProjectIds((old) => {
        if (old?.length === projectIdsFromQuery?.length) {
          return old;
        }

        return projectIdsFromQuery;
      });
    });
  }, [projectIdsFromQuery]);
  // const membershipsStore = useSelector((store: RootState) => store.memberships);
  // const usersStore = useSelector((store: RootState) => store.users);
  const [loading, setIsLoading] = useState(false);
  const [fields, setFields] = useState<FieldWithSource[] | undefined>();

  const getComputedData = useCallback(() => {
    return new Promise<FieldWithSource[] | undefined>((res) => {
      setIsLoading(true);
      queueMicrotask(() => {
        const dataTemp = () => {
          if (data == null || !projectIds) {
            return undefined;
          }

          return data?.fields
            .reduce<FieldWithSource[]>((acc, field) => {
              if (nonFeatureField(field)) {
                acc.push({ ...field, sourceType: getSourceType(field) });
              }

              return acc;
            }, [])
            .sort((fieldA, fieldB) =>
              naturalCompare(fieldA.fieldLabel || '', fieldB.fieldLabel || ''),
            );
        };

        res(dataTemp());
      });
    });
  }, [data, projectIds]);

  useEffect(() => {
    (async () => {
      getComputedData().then((data2) => {
        setIsLoading(false);

        setFields(data2);
      });
    })();
  }, [getComputedData]);

  return {
    data: fields,
    isLoading: fields == null || loading || isProjectFieldsLoading,
  };
};

export const useProjectCustomFields = (
  filterFieldsBy: (f: Field) => boolean = nonDefaultField,
) => {
  const isCurrentUserPartner = useIsCurrentUserPartner();
  const isPermissivePartner = useDoesPartnerMatchPermissionName(
    PartnerPermissionName.Permissive,
  );
  const { data, isLoading } = useProjectFields();
  const fields = useMemo(() => {
    if (!isLoading) {
      if (isCurrentUserPartner && !isPermissivePartner) {
        // Show only status field & shared custom fields to MODERATE & RESTRICTED partners
        return (data ?? []).filter((f) => statusField(f) || !f.private);
      }

      return (data ?? []).filter(filterFieldsBy);
    }

    return [];
  }, [
    isCurrentUserPartner,
    isPermissivePartner,
    isLoading,
    data,
    filterFieldsBy,
  ]);

  return {
    data: fields,
    isLoading,
  };
};

export const useKeyEvents = (): Field[] => {
  const { data } = useKeyEventsFieldsQueryV2();

  return (
    data?.fields?.filter(
      (field) =>
        field.fieldType === KeyEventsAndIntervalFieldTypes.KeyEvents &&
        field.metaData?.relatedFieldId,
    ) ?? []
  );
};

export const useAllKeyEvents = (): Field[] => {
  const { data } = useKeyEventsFieldsQueryV2();

  return (
    data?.fields?.filter(
      (field) => field.fieldType === KeyEventsAndIntervalFieldTypes.KeyEvents,
    ) ?? []
  );
};

export const useIntervals = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}): Field[] => {
  const { data } = useIntervalsFieldsQueryV2({ enabled });

  return (
    data?.fields?.filter(
      (field) =>
        field.fieldType === KeyEventsAndIntervalFieldTypes.Intervals &&
        field.metaData?.relatedFieldId,
    ) ?? []
  );
};

export const useAllIntervals = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}): Field[] => {
  const { data } = useIntervalsFieldsQueryV2({ enabled });

  return (
    data?.fields?.filter(
      (field) => field.fieldType === KeyEventsAndIntervalFieldTypes.Intervals,
    ) ?? []
  );
};
